/**
 * Helpers for the top-bar household member search UI (ENG-2274).
 *
 * - `tokenizeSearch(raw)` mirrors the backend tokenization so we can highlight
 *   each meaningful token independently in result rows.
 * - `matchLabels(matchedFields, raw)` maps the backend `matched_fields` keys
 *   into short human strings for the subtle "Matched: ..." caption under
 *   each result row.
 */

const MAX_INPUT_LEN = 200;
const MAX_TOKENS = 6;
const MIN_TOKEN_LEN = 2;

// Strip ONLY leading and trailing punctuation. Internal punctuation is
// preserved so `Smith(Jr)` stays `Smith(Jr)` (and will icontains-match a
// stored value containing the same parens).
const TOKEN_STRIP_RE = /^[^\w@+\-.']+|[^\w@+\-.']+$/gu;

export function tokenizeSearch(raw) {
  if (!raw || typeof raw !== 'string') {
    return [];
  }
  const capped = raw.slice(0, MAX_INPUT_LEN);
  const tokens = [];
  capped.split(/\s+/).forEach((piece) => {
    if (!piece) {
      return;
    }
    const cleaned = piece.replace(TOKEN_STRIP_RE, '');
    if (cleaned.length >= MIN_TOKEN_LEN) {
      tokens.push(cleaned);
    }
  });
  return tokens.slice(0, MAX_TOKENS);
}

// Order is stable so the "Matched: ..." caption reads consistently.
const LABEL_ORDER = [
  'first_name',
  'last_name',
  'middle_name',
  'household_name',
  'hoh_name',
  'email',
  'phone',
  'cert_category',
  'log_number',
];

function phoneHint(raw) {
  if (!raw) {
    return 'phone';
  }
  const digits = raw.replace(/\D+/g, '');
  if (digits.length < 4) {
    return 'phone';
  }
  return `phone ending ${digits.slice(-4)}`;
}

/**
 * Map matched_fields keys to human strings.
 *
 * Returns an array of { key, label } in stable order. Callers can format it
 * as "Matched: name, household" etc.
 *
 * @param {string[]} matchedFields - keys returned by the backend
 * @param {string} rawSearch - original search string (used for phone hint)
 * @param {{suppressNameIfObvious?: boolean}} options
 */
export function matchLabels(matchedFields, rawSearch, options = {}) {
  if (!Array.isArray(matchedFields) || matchedFields.length === 0) {
    return [];
  }
  const { suppressNameIfObvious = true } = options;
  const set = new Set(matchedFields);
  const out = [];
  LABEL_ORDER.forEach((key) => {
    if (!set.has(key)) {
      return;
    }
    let label;
    switch (key) {
      case 'first_name':
      case 'last_name':
      case 'middle_name':
        label = 'name';
        break;
      case 'household_name':
        label = 'household';
        break;
      case 'hoh_name':
        label = 'head of household';
        break;
      case 'email':
        label = 'email';
        break;
      case 'phone':
        label = phoneHint(rawSearch);
        break;
      case 'cert_category':
        label = 'cert type';
        break;
      case 'log_number':
        label = 'log number';
        break;
      default:
        return;
    }
    if (!out.some((entry) => entry.label === label)) {
      out.push({ key, label });
    }
  });

  // Suppression rule: if the only reason is a direct name match and the name
  // is already visibly highlighted on the row, don't add a redundant caption.
  if (suppressNameIfObvious) {
    const nonNameLabels = out.filter((entry) => entry.label !== 'name');
    const hasOnlyName = out.length > 0 && nonNameLabels.length === 0;
    if (hasOnlyName) {
      return [];
    }
  }

  return out;
}

/**
 * Split a string into an array of { text, matched } segments for rendering.
 * Each token in `tokens` is matched case-insensitively. Overlapping matches
 * are merged.
 *
 * @param {string} text
 * @param {string[]} tokens
 * @returns {Array<{text: string, matched: boolean}>}
 */
export function splitHighlights(text, tokens) {
  if (!text) {
    return [];
  }
  const validTokens = (tokens || []).filter((t) => t && t.length >= 1);
  if (validTokens.length === 0) {
    return [{ text, matched: false }];
  }

  // Build a sorted list of non-overlapping [start, end) ranges where any token matches.
  const lower = text.toLowerCase();
  const ranges = [];
  validTokens.forEach((tok) => {
    const needle = tok.toLowerCase();
    let idx = 0;
    while (idx <= lower.length - needle.length) {
      const hit = lower.indexOf(needle, idx);
      if (hit < 0) {
        break;
      }
      ranges.push([hit, hit + needle.length]);
      idx = hit + needle.length;
    }
  });
  if (ranges.length === 0) {
    return [{ text, matched: false }];
  }
  ranges.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged = [];
  ranges.forEach(([start, end]) => {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) {
      last[1] = Math.max(last[1], end);
    } else {
      merged.push([start, end]);
    }
  });

  const segments = [];
  let cursor = 0;
  merged.forEach(([start, end]) => {
    if (start > cursor) {
      segments.push({ text: text.slice(cursor, start), matched: false });
    }
    segments.push({ text: text.slice(start, end), matched: true });
    cursor = end;
  });
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), matched: false });
  }
  return segments;
}
